import { cloneDeep } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    MaterialIdsType,
    type ILayerProps,
    type IVectorLayerProps,
    type ListStyleStruct,
    type VectorTextInfo,
} from '@/version-types';
import { pushHistory, resolveVectorTextLayout, updateLayer } from '../utils';

export interface IListText {
    /**
     * 应用列表文本
     *
     * @param layerID - 图层 ID
     * @param data - 列表样式数据
     */
    applyListText(layerID: string, data: ListStyleStruct): ILayerProps | void;

    /**
     * 取消设置列表文本
     *
     * @param layerID - 图层 ID
     */
    cancelListText(layerID: string): ILayerProps | void;
}

export class ListText implements IListText {
    constructor(private framework: IFramework) {}

    /**
     * 更新字符型列表声明、素材记录与最终布局。
     *
     * @param layerID - 目标 Vector Layer ID。
     * @param data - 列表配置；null 表示取消列表。
     * @returns 更新后的 Vector；输入不适用或值未变化时返回 void。
     */
    private set(layerID: string, data: ListStyleStruct | null) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const layerData = prevLayer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo || textInfo.listStyle === data?.style) return;

        // Vector Text 只支持与正文共用字体的字符 marker；素材 marker 保持 API 签名但不写入数据。
        if (data?.style === 'custom') return;

        const nextTextInfo: VectorTextInfo = { ...textInfo, listStyle: data ? data.style : '' };
        const nextLayerData: IVectorLayerProps = { ...layerData, textInfo: nextTextInfo };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // 更新素材 id
        const nextMaterialIdMap = nextLayer.materialIds ? cloneDeep(nextLayer.materialIds) : {};
        const nextTextListMaterials = data ? [data.id] : [];
        nextMaterialIdMap[MaterialIdsType.TEXT_LIST] = nextTextListMaterials;
        nextLayer.materialIds = nextMaterialIdMap;
        nextLayer = resolveVectorTextLayout(this.framework, nextLayer);

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].listStyle);

        return nextLayer;
    }

    applyListText(layerID: string, data: ListStyleStruct) {
        return this.set(layerID, data);
    }

    cancelListText(layerID: string) {
        return this.set(layerID, null);
    }
}
